import { useCallback, useMemo } from "react";

import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay
} from "@chakra-ui/react";

import countryList from "country-list";
import { useDispatch, useSelector } from "react-redux";

import {
  Client,
  CreateClientPayload
} from "@/jenesys-types/jenesys-entities/config";
import { Option } from "@/jenesys-types/list";

import { Form } from "@/components/forms/Form/Form";
import { FormActions } from "@/components/forms/FormActions/FormActions";
import { FormInput } from "@/components/forms/FormBaseInput/FormBaseInput";
import { FormField } from "@/components/forms/FormField/FormField";
import { FormSelect } from "@/components/forms/FormSelect/FormSelect";
import { FormStack } from "@/components/forms/FormStack/FormStack";
import useNamedNavigate from "@/hooks/navigation/useNamedNavigate";
import { useCompany } from "@/hooks/util/useCompany";
import {
  changeClient,
  createClient,
  selectIsClientsLoading
} from "@/store/features/companies/companies";
import { generateTestId } from "@/util/testIds";

interface FormData {
  organisation_name: string;
  accounting_platform: string;
  is_vat_registered: string;
  reg_country: string;
}

const defaultClientDetails: FormData = {
  organisation_name: "",
  accounting_platform: "",
  is_vat_registered: "true",
  reg_country: "GB"
};

const booleanOptions: Array<Option> = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" }
];

// Inner form component to use form context
const ClientForm = () => {
  const countries = countryList
    .getData()
    .sort((a, b) => a.name.localeCompare(b.name));

  const countryOptions: Array<Option> = useMemo(
    () =>
      countries.map((country) => ({
        value: country.code,
        label: country.name
      })),
    [countries]
  );

  return (
    <FormStack
      w="full"
      spacing={3}
      mt={0}
      rounded={6}
      borderColor="gray.200"
      data-testid={generateTestId.form("stack", "add-client")}
    >
      <FormField
        name="organisation_name"
        label="Client Organisation Name"
        control={FormInput}
        isRequired
        helpText="Please don't use any of the characters, ', -, &, !, ^ or *"
        placeholder="eg. Vimbo Company"
        data-testid={generateTestId.form("field", "organisation-name")}
      />
      <FormField
        name="is_vat_registered"
        label="Is VAT Registered?"
        control={FormSelect}
        options={booleanOptions}
        isRequired
        data-testid={generateTestId.form("field", "vat-registered")}
      />
      <FormField
        name="reg_country"
        label="Country"
        control={FormSelect}
        options={countryOptions}
        isRequired
        data-testid={generateTestId.form("field", "country")}
      />
    </FormStack>
  );
};

interface AddClientProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddClient: React.FC<AddClientProps> = ({ isOpen, onClose }) => {
  const { activeCompanyId } = useCompany();
  const isClientsLoading = useSelector(selectIsClientsLoading);

  const dispatch = useDispatch();
  const navigate = useNamedNavigate();

  const onCloseMenu = useCallback(
    (client: Client) => {
      onClose();
      dispatch(changeClient(client.id));
      navigate("client", { clientId: client.id });
    },
    [onClose, dispatch, navigate]
  );

  if (!isOpen) return null;

  const onSubmit = (formData: FormData) => {
    const clientData: CreateClientPayload = {
      ...formData,
      chat_platform: "default_platform",
      jackpod_client: false,
      is_vat_registered: formData.is_vat_registered === "true"
    };

    dispatch(
      createClient({ clientData, companyId: activeCompanyId }, onCloseMenu)
    );
  };

  return (
    <Modal
      isCentered
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      data-testid={generateTestId.modal("add-client")}
    >
      <ModalOverlay data-testid={generateTestId.modal("add-client-overlay")} />
      <ModalContent
        px={2}
        py={4}
        w={[351, 460, 460]}
        data-testid={generateTestId.modal("add-client-content")}
      >
        <ModalHeader data-testid={generateTestId.modal("add-client-header")}>
          Add a client
        </ModalHeader>
        <ModalCloseButton
          top={8}
          right={5}
          data-testid={generateTestId.button("close", "add-client-modal")}
        />
        <ModalBody
          fontWeight={500}
          pb={6}
          data-testid={generateTestId.modal("add-client-body")}
        >
          <Box w="full">
            <Form
              onSubmit={onSubmit}
              initialValues={defaultClientDetails}
              data-testid={generateTestId.form("container", "add-client")}
            >
              <ClientForm />
              <ModalFooter
                justifyContent="flex-end"
                mt={4}
                px={0}
                data-testid={generateTestId.modal("add-client-footer")}
              >
                <FormActions
                  submitText="Create Client"
                  cancelText="Cancel"
                  onCancel={onClose}
                  isSaving={isClientsLoading}
                  data-testid={generateTestId.section(
                    "add-client-form-actions"
                  )}
                />
              </ModalFooter>
            </Form>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
